import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
  Button,
  Badge,
  Progress,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getTransportAnalyticsReport, exportTransportAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class TransportAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      analyticsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "Transport Analytics", link: "#" },
      ],
    };
  }

  getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  };

  getDefaultEndDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    this.setState({ loading: true });
    const { startDate, endDate } = this.state;

    const response = await getTransportAnalyticsReport({ startDate, endDate });
    this.setState({
      analyticsData: response?.success ? response.data : null,
      loading: false,
    });
  };

  handleDateChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleApplyFilters = () => {
    this.loadAnalytics();
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { startDate, endDate } = this.state;
      const res = await exportTransportAnalytics({ startDate, endDate });
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  getCategoryBadgeColor = (category) => {
    const colors = {
      airport: "info",
      intercity: "primary",
      outstation: "success",
    };
    return colors[category?.toLowerCase()] || "secondary";
  };

  getStatusBadgeColor = (status) => {
    const colors = {
      completed: "success",
      cancelled: "danger",
      pending: "warning",
      confirmed: "info",
      in_progress: "primary",
    };
    return colors[status?.toLowerCase()] || "secondary";
  };

  render() {
    const { loading, startDate, endDate, analyticsData } = this.state;

    const summary = analyticsData?.summary || {};
    const byCategory = analyticsData?.byCategory || [];
    const byVehicleType = analyticsData?.byVehicleType || [];
    const byStatus = analyticsData?.byStatus || [];
    const completionRateData = analyticsData?.completionRate || {};

    // Calculate completion rate percentage
    const totalRequests = completionRateData.total || summary.totalRequests || 0;
    const completedCount = completionRateData.completed || 0;
    const cancelledCount = completionRateData.cancelled || 0;
    const completionRate = totalRequests > 0 ? (completedCount / totalRequests) * 100 : 0;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Transport Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        name="startDate"
                        value={startDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        name="endDate"
                        value={endDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <Button color="primary" onClick={this.handleApplyFilters}>
                      Apply
                    </Button>
                    <Button color="success" className="ml-2" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                  </Col>
                  <Col md={4} className="text-right">
                    <Link to="/analytics" className="btn btn-outline-secondary">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading transport analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Requests</h6>
                        <h3>{this.formatNumber(summary.totalRequests)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-success text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Revenue</h6>
                        <h3>{this.formatCurrency(summary.totalRevenue)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Avg Trip Value</h6>
                        <h3>{this.formatCurrency(summary.avgTripValue)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-warning text-white">
                      <CardBody>
                        <h6 className="text-white-50">Completion Rate</h6>
                        <h3>{completionRate.toFixed(1)}%</h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  {/* By Service Category */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Requests by Category</h4>
                        {byCategory.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Category</th>
                                <th className="text-right">Requests</th>
                                <th className="text-right">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {byCategory.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <Badge color={this.getCategoryBadgeColor(item.category || item._id)}>
                                      {item.category || item._id || "Unknown"}
                                    </Badge>
                                  </td>
                                  <td className="text-right">{this.formatNumber(item.requests || item.count)}</td>
                                  <td className="text-right">{this.formatCurrency(item.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* By Vehicle Type */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Requests by Vehicle Type</h4>
                        {byVehicleType.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Vehicle Type</th>
                                <th className="text-right">Requests</th>
                                <th className="text-right">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {byVehicleType.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <i className="ri-car-line mr-1"></i>
                                    {item.name || item.vehicleName || item._id || "Unknown"}
                                  </td>
                                  <td className="text-right">{this.formatNumber(item.requests || item.count)}</td>
                                  <td className="text-right">{this.formatCurrency(item.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* By Status */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Requests by Status</h4>
                    {byStatus.length === 0 ? (
                      <p className="text-muted text-center">No data available</p>
                    ) : (
                      <Row>
                        {byStatus.map((item, index) => {
                          const total = byStatus.reduce((sum, s) => sum + s.count, 0);
                          const percentage = total > 0 ? (item.count / total) * 100 : 0;
                          return (
                            <Col md={3} key={index}>
                              <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                  <Badge color={this.getStatusBadgeColor(item._id)}>
                                    {item._id || "Unknown"}
                                  </Badge>
                                  <span>{this.formatNumber(item.count)}</span>
                                </div>
                                <Progress
                                  value={percentage}
                                  color={this.getStatusBadgeColor(item._id)}
                                  style={{ height: "8px" }}
                                />
                                <small className="text-muted">{percentage.toFixed(1)}%</small>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </CardBody>
                </Card>

                {/* Additional Metrics */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Performance Metrics</h4>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <p className="mb-1">Completion Rate</p>
                          <Progress
                            value={completionRate}
                            color={completionRate > 80 ? "success" : completionRate > 60 ? "warning" : "danger"}
                            style={{ height: "20px" }}
                          >
                            {completionRate.toFixed(1)}%
                          </Progress>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <p className="mb-1">Completed Requests</p>
                          <h5>{this.formatNumber(completedCount)}</h5>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <p className="mb-1">Cancelled Requests</p>
                          <h5 className="text-danger">{this.formatNumber(cancelledCount)}</h5>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(TransportAnalytics));
